<template>
  <div class="login-page">
    <b-container class="login-shell">
      <b-row class="login-frame no-gutters">
        <b-col lg="5" class="d-none d-lg-flex">
          <section class="brand-panel">
            <p class="eyebrow">Secure Reset</p>
            <h1>Choose a new password and return to your workspace securely.</h1>
            <p class="brand-copy">
              This recovery page is used only from the email reset link, so you can safely set a new
              password without signing in first.
            </p>

            <div class="brand-metrics">
              <div class="metric-card">
                <span class="metric-value">Unauthenticated</span>
                <span class="metric-label">
                  The reset link verifies the request so you can complete recovery outside the app.
                </span>
              </div>
              <div class="metric-card">
                <span class="metric-value">Protected</span>
                <span class="metric-label">
                  Your new password is submitted securely and your old password is replaced only
                  after token verification succeeds.
                </span>
              </div>
            </div>
          </section>
        </b-col>

        <b-col cols="12" lg="7">
          <section class="form-panel">
            <div class="form-panel__inner">
              <div class="mobile-brand d-lg-none">
                <p class="eyebrow mb-2">Secure Reset</p>
                <h1>Create a new password</h1>
              </div>

              <div class="form-header">
                <p class="form-kicker">Reset password</p>
                <h2>Set your new password</h2>
                <p class="form-subtitle">
                  Enter a strong new password below. Once the reset succeeds, you will be redirected
                  to the login page.
                </p>
              </div>

              <div
                v-if="validationMessage"
                class="status-alert status-alert--success status-alert--soft"
                role="alert"
              >
                {{ validationMessage }}
              </div>

              <div v-if="successMessage" class="status-alert status-alert--success" role="alert">
                {{ successMessage }}
              </div>

              <div v-if="errorMessage" class="status-alert status-alert--danger" role="alert">
                {{ errorMessage }}
              </div>

              <ValidationObserver ref="resetObserver">
                <form class="login-form" @submit.prevent="submitReset">
                  <ValidationProvider
                    v-slot="{ errors, ariaInput, ariaMsg }"
                    vid="reset-password"
                    name="New password"
                    rules="required|min:8"
                  >
                    <div class="premium-field">
                      <label class="field-label" for="reset-password">
                        New password
                        <span class="required-mark">*</span>
                      </label>
                      <p class="field-description">
                        Use at least 8 characters for a stronger password.
                      </p>
                      <div class="field-input-wrap">
                        <b-form-input
                          id="reset-password"
                          v-model="form.new_password"
                          :class="[fieldClasses(errors), 'premium-input--with-toggle']"
                          :type="showPassword ? 'text' : 'password'"
                          placeholder="Create a new password"
                          autocomplete="new-password"
                          v-bind="ariaInput"
                        />
                        <button
                          type="button"
                          class="password-toggle"
                          :aria-label="showPassword ? 'Hide password' : 'Show password'"
                          :title="showPassword ? 'Hide password' : 'Show password'"
                          @click="showPassword = !showPassword"
                        >
                          <i
                            :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                            aria-hidden="true"
                          ></i>
                        </button>
                      </div>
                      <p v-if="shouldShowFieldError(errors)" class="field-error" v-bind="ariaMsg">
                        {{ errors[0] }}
                      </p>
                    </div>
                  </ValidationProvider>

                  <ValidationProvider
                    v-slot="{ errors, ariaInput, ariaMsg }"
                    name="Confirm password"
                    rules="required|confirmed:reset-password"
                  >
                    <div class="premium-field">
                      <label class="field-label" for="reset-confirm-password">
                        Confirm password
                        <span class="required-mark">*</span>
                      </label>
                      <p class="field-description">
                        Re-enter the password to make sure both fields match.
                      </p>
                      <div class="field-input-wrap">
                        <b-form-input
                          id="reset-confirm-password"
                          v-model="form.confirm_password"
                          :class="[fieldClasses(errors), 'premium-input--with-toggle']"
                          :type="showConfirmPassword ? 'text' : 'password'"
                          placeholder="Confirm your new password"
                          autocomplete="new-password"
                          v-bind="ariaInput"
                        />
                        <button
                          type="button"
                          class="password-toggle"
                          :aria-label="
                            showConfirmPassword
                              ? 'Hide confirmation password'
                              : 'Show confirmation password'
                          "
                          :title="
                            showConfirmPassword
                              ? 'Hide confirmation password'
                              : 'Show confirmation password'
                          "
                          @click="showConfirmPassword = !showConfirmPassword"
                        >
                          <i
                            :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                            aria-hidden="true"
                          ></i>
                        </button>
                      </div>
                      <p v-if="shouldShowFieldError(errors)" class="field-error" v-bind="ariaMsg">
                        {{ errors[0] }}
                      </p>
                    </div>
                  </ValidationProvider>

                  <button
                    type="submit"
                    class="login-button"
                    :disabled="loading || validatingToken || !tokenValidated"
                  >
                    <b-spinner v-show="loading" small class="mr-2" />
                    <span>{{
                      validatingToken
                        ? 'Verifying link...'
                        : loading
                        ? 'Updating password...'
                        : 'Reset my password'
                    }}</span>
                  </button>

                  <div class="auth-switch">
                    <span class="mr-1">Need to return instead?</span>
                    <b-link class="text-decoration-none" to="/auth/login">Back to login</b-link>
                  </div>
                </form>
              </ValidationObserver>
            </div>
          </section>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import {
  VALIDATE_RESET_PASSWORD_TOKEN,
  VERIFY_RESET_PASSWORD_TOKEN,
} from '../../ngo/config/api-modules/api.auth';

export default {
  name: 'VerifyResetPassword',
  data() {
    return {
      token: this.$route.params.token,
      form: {
        new_password: '',
        confirm_password: '',
      },
      loading: false,
      validatingToken: false,
      tokenValidated: false,
      showPassword: false,
      showConfirmPassword: false,
      validationMessage: '',
      successMessage: '',
      errorMessage: '',
    };
  },
  mounted() {
    this.validateToken();
  },
  methods: {
    fieldClasses(errors) {
      return ['premium-input', { 'premium-input--error': this.shouldShowFieldError(errors) }];
    },
    shouldShowFieldError(errors) {
      return Array.isArray(errors) && errors.length > 0;
    },
    validateToken() {
      this.validatingToken = true;
      this.tokenValidated = false;
      this.validationMessage = '';
      this.errorMessage = '';

      VALIDATE_RESET_PASSWORD_TOKEN({
        token: this.token,
      })
        .then(() => {
          this.tokenValidated = true;
          this.validationMessage = 'Reset link verified. You can now choose a new password.';
        })
        .catch((error) => {
          this.tokenValidated = false;
          this.errorMessage =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'This reset link is invalid or has expired.';
        })
        .finally(() => {
          this.validatingToken = false;
        });
    },
    async submitReset() {
      this.successMessage = '';
      this.errorMessage = '';

      if (!this.tokenValidated) {
        this.errorMessage = 'Reset link verification is required before changing your password.';
        return;
      }

      const isValid = await this.$refs.resetObserver.validate();
      if (!isValid) {
        return;
      }

      this.loading = true;

      VERIFY_RESET_PASSWORD_TOKEN({
        password: this.form.new_password,
        token: this.token,
      })
        .then(() => {
          this.successMessage = 'Password updated successfully. Redirecting to login...';
          this.$toast.show({
            type: 'SUCCESS',
            message: 'Password Updated',
          });
          setTimeout(() => {
            this.$router.push({ path: '/auth/login' });
          }, 3000);
        })
        .catch((error) => {
          this.errorMessage =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Unable to reset password with this link.';
          this.$toast.show({
            type: 'ERROR',
            message: this.errorMessage,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.login-page {
  position: relative;
  min-height: 100vh;
  padding: 2rem 0;
  background: radial-gradient(circle at top left, rgba(48, 171, 76, 0.2), transparent 28%),
    radial-gradient(circle at bottom right, rgba(13, 34, 20, 0.18), transparent 30%),
    linear-gradient(135deg, #f3f8f3 0%, #edf3ee 45%, #e4ece5 100%);
}

.login-page::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgba(255, 255, 255, 0.26) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.26) 1px, transparent 1px);
  background-size: 120px 120px;
  opacity: 0.45;
  pointer-events: none;
}

.login-shell {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  min-height: calc(100vh - 4rem);
}

.login-frame {
  width: 100%;
  overflow: hidden;
  border: 1px solid rgba(16, 46, 25, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 30px 90px rgba(20, 43, 27, 0.15);
  backdrop-filter: blur(14px);
}

.brand-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100%;
  padding: 3.5rem 3rem;
  color: #f8fff8;
  background: linear-gradient(180deg, rgba(5, 20, 10, 0.12), rgba(5, 20, 10, 0.2)),
    linear-gradient(155deg, #0d3418 0%, #195426 52%, #30ab4c 100%);
}

.brand-panel::after {
  content: '';
  position: absolute;
  width: 14rem;
  height: 14rem;
  right: -3rem;
  bottom: -4rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  filter: blur(2px);
}

.eyebrow,
.form-kicker {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.eyebrow::before,
.form-kicker::before {
  content: '';
  width: 2.5rem;
  height: 1px;
  background: currentColor;
  opacity: 0.65;
}

.brand-panel h1,
.mobile-brand h1,
.form-header h2 {
  margin: 0;
  font-size: clamp(2rem, 3vw, 3rem);
  line-height: 1.1;
  letter-spacing: -0.04em;
}

.brand-copy,
.form-subtitle {
  margin: 1.15rem 0 0;
  font-size: 1rem;
  line-height: 1.75;
}

.brand-copy {
  max-width: 30rem;
  color: rgba(248, 255, 248, 0.82);
}

.brand-metrics {
  display: grid;
  gap: 1rem;
  margin-top: 2rem;
}

.metric-card {
  padding: 1.2rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
}

.metric-value {
  display: block;
  margin-bottom: 0.45rem;
  font-size: 1rem;
  font-weight: 700;
}

.metric-label {
  color: rgba(248, 255, 248, 0.78);
  line-height: 1.6;
}

.form-panel {
  display: flex;
  min-height: 100%;
  align-items: center;
  justify-content: center;
  padding: 2.4rem 2rem;
}

.form-panel__inner {
  width: 100%;
  max-width: 34rem;
}

.mobile-brand {
  margin-bottom: 1.5rem;
}

.form-header {
  margin-bottom: 1.75rem;
}

.form-kicker {
  color: #2d8f45;
}

.form-header h2 {
  color: #143020;
  font-size: clamp(1.85rem, 2.4vw, 2.45rem);
}

.form-subtitle {
  color: #62736a;
}

.status-alert {
  margin-bottom: 1.15rem;
  padding: 0.95rem 1rem;
  border-radius: 16px;
  line-height: 1.6;
}

.status-alert--success {
  background: rgba(48, 171, 76, 0.12);
  color: #1f6b35;
  border: 1px solid rgba(48, 171, 76, 0.18);
}

.status-alert--soft {
  background: rgba(34, 117, 58, 0.08);
}

.status-alert--danger {
  background: rgba(194, 79, 79, 0.1);
  color: #a44747;
  border: 1px solid rgba(194, 79, 79, 0.14);
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.premium-field {
  margin-bottom: 0.35rem;
}

.field-label {
  display: block;
  margin-bottom: 0.4rem;
  font-weight: 700;
  color: #173221;
}

.required-mark {
  color: #c24f4f;
}

.field-description {
  margin-bottom: 0.5rem;
  color: #6a7b72;
  line-height: 1.6;
}

.field-input-wrap {
  position: relative;
}

.premium-input {
  min-height: 3.35rem;
  border: 1px solid rgba(21, 58, 31, 0.12);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);
}

.premium-input:focus {
  border-color: rgba(48, 171, 76, 0.55);
  box-shadow: 0 0 0 4px rgba(48, 171, 76, 0.12);
}

.premium-input--error {
  border-color: rgba(194, 79, 79, 0.45);
}

.premium-input--with-toggle {
  padding-right: 3.35rem;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 0.85rem;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #668072;
}

.field-error {
  margin: 0.45rem 0 0;
  color: #b54a4a;
  font-size: 0.84rem;
}

.login-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 3.45rem;
  margin-top: 0.4rem;
  border: 0;
  border-radius: 18px;
  background: linear-gradient(135deg, #16391f 0%, #30ab4c 100%);
  box-shadow: 0 16px 30px rgba(48, 171, 76, 0.18);
  color: #fff;
  font-weight: 700;
  letter-spacing: 0.01em;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.login-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 20px 36px rgba(48, 171, 76, 0.22);
}

.login-button:disabled {
  opacity: 0.72;
  cursor: not-allowed;
}

.auth-switch {
  margin-top: 1rem;
  text-align: center;
  color: #64766c;
}

.auth-switch a {
  color: #22753a;
  font-weight: 700;
}

@media (max-width: 991px) {
  .login-page {
    padding: 1rem 0;
  }

  .login-shell {
    min-height: calc(100vh - 2rem);
  }

  .form-panel {
    padding: 2rem 1.25rem;
  }
}

@media (max-width: 575px) {
  .login-frame {
    border-radius: 22px;
  }

  .form-panel {
    padding: 1.5rem 1rem;
  }

  .form-header h2,
  .mobile-brand h1 {
    font-size: 1.85rem;
  }
}
</style>
